import React, { useCallback, useEffect, useState } from "react";
import { Moment } from "moment";
import { Navigate } from "react-router-dom";
import Table from "../../../core/common/dataTable";
import PredefinedDateRanges from "../../../core/common/datePicker";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { getAadharServiceLogs } from "../../../apis/aadharServiceLogs";

type LogItem = {
  id: number;
  user_name?: string;
  aadhar_number?: string;
  service_type?: string;
  createdAt?: string;
  user?: { username?: string };
  financer?: { full_name?: string };
};

type Summary = {
  total: number;
  otp_send: number;
  aadhar_verify: number;
};

type TokenSummary = {
  aadhar_token_limit: number;
  aadhar_token_used: number;
  aadhar_token_balance: number;
};

const serviceLabel = (value?: string) => {
  if (value === "OTP_SEND") return "OTP Sent";
  if (value === "AADHAAR_VERIFY") return "Aadhaar Verified";
  return value || "--";
};

const formatDateTime = (value?: string) => {
  if (!value) return "--";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "--"
    : date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const AadharServiceLogs = () => {
  const userInfo = JSON.parse(sessionStorage.getItem("info") || "{}");
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [summary, setSummary] = useState<Summary>({
    total: 0,
    otp_send: 0,
    aadhar_verify: 0,
  });
  const [tokenSummary, setTokenSummary] = useState<TokenSummary>({
    aadhar_token_limit: 0,
    aadhar_token_used: 0,
    aadhar_token_balance: 0,
  });
  const [search, setSearch] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState<{
    start: Moment | undefined;
    end: Moment | undefined;
  }>({ start: undefined, end: undefined });

  const fetchLogs = useCallback(async () => {
    if (userInfo.type !== "financer") {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await getAadharServiceLogs({
        company_id: userInfo.id,
        start_date: dateRange.start?.format("MM/DD/YYYY"),
        end_date: dateRange.end?.format("MM/DD/YYYY"),
        service_type: serviceType,
        search: search.trim(),
      });
      setLogs(response?.data || []);
      setSummary(
        response?.summary || { total: 0, otp_send: 0, aadhar_verify: 0 }
      );
      setTokenSummary(
        response?.tokenSummary || {
          aadhar_token_limit: 0,
          aadhar_token_used: 0,
          aadhar_token_balance: 0,
        }
      );
    } catch (requestError) {
      console.error("Unable to load Aadhaar service logs:", requestError);
      setLogs([]);
      setSummary({ total: 0, otp_send: 0, aadhar_verify: 0 });
      setTokenSummary({
        aadhar_token_limit: 0,
        aadhar_token_used: 0,
        aadhar_token_balance: 0,
      });
      setError("Unable to load Aadhaar service logs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    dateRange.end,
    dateRange.start,
    search,
    serviceType,
    userInfo.id,
    userInfo.type,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(fetchLogs, 300);
    return () => window.clearTimeout(timer);
  }, [fetchLogs]);

  const columns = [
    {
      title: "Sr No",
      key: "serial",
      render: (_: unknown, __: LogItem, index: number) => index + 1,
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      render: (value: string) => formatDateTime(value),
      sorter: (a: LogItem, b: LogItem) =>
        new Date(a.createdAt || 0).getTime() -
        new Date(b.createdAt || 0).getTime(),
    },
    {
      title: "User",
      key: "user",
      render: (_: unknown, record: LogItem) =>
        record.user_name || record.user?.username || "--",
    },
    {
      title: "Aadhaar Number",
      dataIndex: "aadhar_number",
      render: (value: string) => value || "--",
    },
    {
      title: "Service",
      dataIndex: "service_type",
      render: (value: string) => (
        <span
          className={`badge ${
            value === "AADHAAR_VERIFY"
              ? "badge-success-transparent"
              : "badge-info-transparent"
          }`}
        >
          {serviceLabel(value)}
        </span>
      ),
    },
    {
      title: "Company",
      key: "company",
      render: (_: unknown, record: LogItem) =>
        record.financer?.full_name || "--",
    },
  ];

  if (userInfo.type !== "financer") {
    return <Navigate to="/index" replace />;
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div>
            <h2 className="mb-1">Aadhaar Service Logs</h2>
            <p className="mb-0 text-muted">
              Track Aadhaar OTP and verification service usage.
            </p>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="row mb-3">
          {[
            ["Total Requests", summary.total, "ti-list-details"],
            ["OTP Sent", summary.otp_send, "ti-message-2"],
            ["Verified", summary.aadhar_verify, "ti-shield-check"],
            ["Aadhar Token", tokenSummary.aadhar_token_limit, "ti-coin"],
            [
              "Balance Aadhar Token",
              tokenSummary.aadhar_token_balance,
              "ti-wallet",
            ],
          ].map(([label, value, icon]) => (
            <div className="col-md-4 mb-3" key={String(label)}>
              <div className="card h-100 mb-0">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-muted mb-1">{label}</p>
                    <h3 className="mb-0">{value}</h3>
                  </div>
                  <i className={`ti ${icon} fs-28 text-primary`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="row g-3 align-items-end">
              <div className="col-lg-4 col-md-6">
                <label className="form-label">Search</label>
                <input
                  className="form-control"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search user, Aadhaar last digits or service"
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="form-label">Service Type</label>
                <select
                  className="form-select"
                  value={serviceType}
                  onChange={(event) => setServiceType(event.target.value)}
                >
                  <option value="">All Services</option>
                  <option value="OTP_SEND">OTP Sent</option>
                  <option value="AADHAAR_VERIFY">Aadhaar Verified</option>
                </select>
              </div>
              <div className="col-lg-4 col-md-6">
                <label className="form-label">Date Range</label>
                <PredefinedDateRanges
                  state={dateRange}
                  setState={setDateRange}
                />
              </div>
              <div className="col-lg-1 col-md-6">
                <button
                  className="btn btn-outline-primary w-100"
                  type="button"
                  onClick={fetchLogs}
                  title="Refresh"
                >
                  <i className="ti ti-refresh" />
                </button>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : error ? (
              <div className="alert alert-danger m-3">{error}</div>
            ) : logs.length ? (
              <Table
                dataSource={logs.map((item) => ({ ...item, key: item.id }))}
                columns={columns}
                Selection={false}
                scroll={{ x: 900 }}
              />
            ) : (
              <div className="text-center text-muted p-5">
                No Aadhaar service logs found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AadharServiceLogs;
